/**
 * API Configuration Utility
 * Provides environment-based API endpoint selection
 */

// Available environments
export const ENVIRONMENTS = {
  DEV: 'DEV',
  TEST: 'TEST',
  PROD: 'PROD'
};

// Environment display names
export const ENVIRONMENT_LABELS = {
  DEV: 'Development',
  TEST: 'Test',
  PROD: 'Production'
};

/**
 * Get all available environments from .env
 * @returns {Array} Array of environment keys
 */
export const getAvailableEnvironments = () => {
  const envString = process.env.REACT_APP_AVAILABLE_ENVIRONMENTS || 'DEV,TEST,PROD';
  return envString.split(',').map(env => env.trim());
};

/**
 * Get the default environment
 * @returns {string} Default environment key
 */
export const getDefaultEnvironment = () => {
  return process.env.REACT_APP_DEFAULT_ENVIRONMENT || 'DEV';
};

/**
 * Get API endpoints for the current deployment environment.
 * Endpoints are baked into the build via unprefixed REACT_APP_* variables
 * generated by Terraform for each environment (dev / test / prod).
 * The `environment` parameter is accepted for backward compatibility but
 * has no effect — each build targets exactly one environment.
 * @returns {Object} Object containing all API endpoints
 */
export const getApiEndpoints = (_environment) => {
  return {
    // Agent Setup API (single agent creation)
    agentApi: process.env.REACT_APP_AGENT_API_ENDPOINT || '',
    agentsView: process.env.REACT_APP_AGENTS_VIEW_API_ENDPOINT || '/admin/view-agents',
    agentActivity: process.env.REACT_APP_AGENT_ACTIVITY_API_ENDPOINT || '/admin/agent-activity',
    queuePerformance: process.env.REACT_APP_QUEUE_PERFORMANCE_API_ENDPOINT || '/admin/queue-performance',
    updateAgentRoutingProfileApi: process.env.REACT_APP_UPDATE_AGENT_ROUTING_PROFILE_API_ENDPOINT || '/admin/update-agent-routing-profile',

    // Bulk Agent Upload API
    bulkAgentApi: process.env.REACT_APP_BULK_AGENT_API_ENDPOINT || '',
    bulkAgentPresignApi: process.env.REACT_APP_BULK_AGENT_PRESIGN_API_ENDPOINT || '',
    bulkStatusApi: process.env.REACT_APP_BULK_AGENT_STATUS_API_ENDPOINT || '',
    bulkJobsListApi: process.env.REACT_APP_BULK_AGENT_LIST_API_ENDPOINT || '',

    // Bulk Agent Update API (reuses presign endpoint with ?type=agent-update)
    bulkAgentUpdateApi: process.env.REACT_APP_BULK_AGENT_UPDATE_API_ENDPOINT || '',

    // Program Data Upload API
    programDataApi: process.env.REACT_APP_PROGRAM_DATA_API_ENDPOINT || '',
    // Note: program data presign reuses bulkAgentPresignApi with ?type=program-data

    //Routing administration APIs
    attributesView: process.env.REACT_APP_ATTRIBUTES_VIEW_API_ENDPOINT || '/admin/view-attributes',
    attributeRegistry: process.env.REACT_APP_ATTRIBUTE_REGISTRY_API_ENDPOINT || '/admin/attribute-registry',
    routingGroups: process.env.REACT_APP_ROUTING_GROUPS_API_ENDPOINT || '/admin/view-routing-groups',
    routingGroupRegistry: process.env.REACT_APP_ROUTING_GROUP_REGISTRY_API_ENDPOINT || '/admin/routing-group-registry',
    deleteRoutingGroup: process.env.REACT_APP_DELETE_ROUTING_GROUP_API_ENDPOINT || '/admin/delete-routing-group',
    routingPolicies: process.env.REACT_APP_ROUTING_POLICIES_API_ENDPOINT || '/admin/view-routing-policies',
    routingPolicyRegistry: process.env.REACT_APP_ROUTING_POLICY_REGISTRY_API_ENDPOINT || '/admin/routing-policy-registry',
    deleteRoutingPolicy: process.env.REACT_APP_DELETE_ROUTING_POLICY_API_ENDPOINT || '/admin/delete-routing-policies',
    routingRestrictions: process.env.REACT_APP_ROUTING_RESTRICTIONS_API_ENDPOINT || '/admin/view-routing-restrictions',
    routingRestrictionRegistry: process.env.REACT_APP_ROUTING_RESTRICTION_REGISTRY_API_ENDPOINT || '/admin/routing-restriction-registry',
    deleteRoutingRestrictions: process.env.REACT_APP_DELETE_ROUTING_RESTRICTIONS_API_ENDPOINT || '/admin/delete-routing-restrictions',
    routingSimulation: process.env.REACT_APP_ROUTING_SIMULATION_API_ENDPOINT || '/admin/routing-simulation',
    routingSimulationRegistry: process.env.REACT_APP_ROUTING_SIMULATION_REGISTRY_API_ENDPOINT || '/admin/routing-simulation-registry',

    //Connect Archive Management
    archiveDataApi: process.env.REACT_APP_ARCHIVE_DATA_API_ENDPOINT || '/admin/archive-data',
    createArchiveApi: process.env.REACT_APP_CREATE_ARCHIVE_API_ENDPOINT || '/admin/create-archive',
    archiveDetailApi: process.env.REACT_APP_ARCHIVE_DETAIL_API_ENDPOINT || '/admin/archive-detail',

    // Connect Object Setup APIs (JSON upload + async processing)
    quickConnectApi: process.env.REACT_APP_QUICK_CONNECT_API_ENDPOINT || '',
    routingProfileApi: process.env.REACT_APP_ROUTING_PROFILE_API_ENDPOINT || '',
    securityProfileApi: process.env.REACT_APP_SECURITY_PROFILE_API_ENDPOINT || '',
    userHierarchyGroupApi: process.env.REACT_APP_USER_HIERARCHY_GROUP_API_ENDPOINT || '',
    sendNotificationApi: process.env.REACT_APP_SEND_NOTIFICATION_API_ENDPOINT || '',

    // Queue Setup API
    queueApi: process.env.REACT_APP_QUEUE_API_ENDPOINT || '',

    // Dropdown APIs
    instanceDropdown: process.env.REACT_APP_INSTANCE_DROPDOWN_API_ENDPOINT || '/dropdown/connect-instances',
    queues: process.env.REACT_APP_QUEUES_DROPDOWN_API_ENDPOINT || '/dropdown/queues',
    routingProfiles: process.env.REACT_APP_ROUTING_PROFILES_API_ENDPOINT || '/dropdown/routing-profiles',
    securityProfiles: process.env.REACT_APP_SECURITY_PROFILES_API_ENDPOINT || '/dropdown/security-profiles',
    userHierarchyGroups: process.env.REACT_APP_HIERARCHY_GROUPS_DROPDOWN_API_ENDPOINT || '/dropdown/hierarchy-groups',
    simulations: process.env.REACT_APP_SIMULATIONS_DROPDOWN_API_ENDPOINT || '/dropdown/simulations',
    
    // User Authentication APIs
    userSignup: process.env.REACT_APP_USER_SIGNUP_API_ENDPOINT || '',
    userLogin: process.env.REACT_APP_USER_LOGIN_API_ENDPOINT || '',
  };
};

/**
 * Validate if environment has all required endpoints configured
 * @param {string} environment - Environment key
 * @returns {Object} Validation result with status and missing endpoints
 */
export const validateEnvironmentConfig = (environment) => {
  const endpoints = getApiEndpoints(environment);
  const missing = [];
  
  Object.entries(endpoints).forEach(([key, value]) => {
    if (!value) {
      missing.push(key);
    }
  });
  
  return {
    isValid: missing.length === 0,
    missing: missing
  };
};

/**
 * Get environment selection from localStorage or default
 * @returns {string} Selected environment key
 */
export const getSelectedEnvironment = () => {
  const stored = localStorage.getItem('selectedEnvironment');
  return stored || getDefaultEnvironment();
};

/**
 * Save environment selection to localStorage
 * @param {string} environment - Environment key to save
 */
export const saveSelectedEnvironment = (environment) => {
  localStorage.setItem('selectedEnvironment', environment);
};

/**
 * Clear saved environment selection
 */
export const clearSelectedEnvironment = () => {
  localStorage.removeItem('selectedEnvironment');
};

/**
 * Get environment badge color for UI display
 * @param {string} environment - Environment key
 * @returns {string} Color code for badge
 */
export const getEnvironmentColor = (environment) => {
  const colors = {
    DEV: '#4CAF50',   // Green
    TEST: '#FF9800',  // Orange
    PROD: '#F44336'   // Red
  };
  return colors[environment] || '#757575'; // Gray default
};

/**
 * Get environment description
 * @param {string} environment - Environment key
 * @returns {string} Description text
 */
export const getEnvironmentDescription = (environment) => {
  const descriptions = {
    DEV: 'Development environment for testing and development',
    TEST: 'Test environment for UAT and validation',
    PROD: 'Production environment - live data'
  };
  return descriptions[environment] || '';
};

const apiConfig = {
  ENVIRONMENTS,
  ENVIRONMENT_LABELS,
  getAvailableEnvironments,
  getDefaultEnvironment,
  getApiEndpoints,
  validateEnvironmentConfig,
  getSelectedEnvironment,
  saveSelectedEnvironment,
  clearSelectedEnvironment,
  getEnvironmentColor,
  getEnvironmentDescription
};

export default apiConfig;
